import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LocalShippingOutlined,
  ArrowForwardRounded,
  RestartAltRounded,
  CheckCircleOutlineRounded,
  BuildOutlined,
  BlockOutlined,
  CircleOutlined,
} from "@mui/icons-material";
import { useVehicleStatus } from "./useVehicleStatus";
import styles from "./VehicleStatus.module.scss";
import { useAppDispatch } from "Redux/store";
import { getVehicleStatus } from "Redux/Dashboard/reducer";
import { UNITS } from "Shared/Constants/routes";

const statusIcon = (key: string) => {
  if (key === "active") return <CheckCircleOutlineRounded />;
  if (key === "in_shop") return <BuildOutlined />;
  if (key === "out_of_service") return <BlockOutlined />;
  return <CircleOutlined />;
};

const interactionColor = (key: string) => {
  if (key === "out_of_service") return "alert";
  if (["in_shop", "downtime"].includes(key)) return "caution";
  return "action";
};

const statusStyle = (key: string, color: string) =>
  ({
    "--status-color": color,
    "--status-active-color": `var(--dashboard-${interactionColor(key)})`,
    "--status-active-fill": `var(--dashboard-${interactionColor(key)}-soft)`,
  }) as React.CSSProperties;

// Separate paths keep the hit area on each visible segment of the ring.
const segmentPath = (start: number, fraction: number) => {
  const gap = fraction < 100 ? Math.min(1.5, fraction * 0.2) : 0;
  const length = fraction - gap;
  const point = (percent: number) => {
    const angle = (percent / 100) * Math.PI * 2 - Math.PI / 2;
    return `${100 + 82 * Math.cos(angle)},${100 + 82 * Math.sin(angle)}`;
  };
  return `M ${point(start)} A 82,82 0 0 1 ${point(start + length / 2)} A 82,82 0 0 1 ${point(start + length)}`;
};

const VehicleStatus: React.FC = () => {
  const { statuses, loading, error } = useVehicleStatus();
  const [selected, setSelected] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getVehicleStatus());
  }, [dispatch]);
  const total = statuses.reduce((sum, item) => sum + item.value, 0);
  const active = statuses.find((item) => item.key === selected);
  const toggleStatus = (key: string) =>
    setSelected((current) => (current === key ? null : key));
  let offset = 0;

  return (
    <div className={styles.block} aria-busy={loading}>
      <header className={styles.heading}>
        <span className={styles.headingIcon}>
          <LocalShippingOutlined aria-hidden="true" />
        </span>
        <div>
          <h2>Vehicle status</h2>
          <p>Explore your fleet by status</p>
        </div>
      </header>
      {loading ? (
        <div className={styles.message} role="status">
          Loading vehicle status…
        </div>
      ) : error ? (
        <div className={styles.message} role="alert">
          <p>Vehicle status could not be loaded.</p>
          <button type="button" onClick={() => dispatch(getVehicleStatus())}>
            <RestartAltRounded aria-hidden="true" /> Try again
          </button>
        </div>
      ) : statuses.length ? (
        <>
          <div className={styles.distribution}>
            <div className={styles.chart}>
              <svg
                viewBox="0 0 200 200"
                role="group"
                aria-label="Vehicle status chart"
              >
                <circle
                  cx="100"
                  cy="100"
                  r="82"
                  className={styles.track}
                  aria-hidden="true"
                />
                {statuses.map((item) => {
                  const fraction = total ? (item.value / total) * 100 : 0;
                  const start = offset;
                  offset += fraction;
                  if (!fraction) return null;
                  return (
                    <path
                      key={item.key}
                      d={segmentPath(start, fraction)}
                      className={styles.arc}
                      style={statusStyle(item.key, item.color)}
                      role="button"
                      tabIndex={0}
                      aria-label={`${item.name}: ${item.value.toLocaleString()} vehicles`}
                      aria-pressed={selected === item.key}
                      onClick={() => toggleStatus(item.key)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleStatus(item.key);
                        }
                      }}
                      opacity={!active || active.key === item.key ? 1 : 0.25}
                    >
                      <title>{`${item.name}: ${item.value.toLocaleString()} vehicles`}</title>
                    </path>
                  );
                })}
              </svg>
              <div className={styles.chartLabel} aria-live="polite">
                <LocalShippingOutlined aria-hidden="true" />
                <strong
                  key={active?.key || "all"}
                  style={{
                    color: active
                      ? `var(--dashboard-${interactionColor(active.key)})`
                      : undefined,
                  }}
                >
                  {(active?.value ?? total).toLocaleString()}
                </strong>
                <span>{active?.name || "Reported vehicles"}</span>
              </div>
            </div>
            <div
              className={styles.legend}
              aria-label="Explore vehicle statuses"
            >
              {statuses.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  aria-pressed={selected === item.key}
                  onClick={() => toggleStatus(item.key)}
                  style={statusStyle(item.key, item.color)}
                >
                  <span className={styles.statusIcon} aria-hidden="true">
                    {statusIcon(item.key)}
                  </span>
                  <span>{item.name}</span>
                  <strong>{item.value.toLocaleString()}</strong>
                </button>
              ))}
            </div>
          </div>
          <footer className={styles.footer}>
            <span>
              {active
                ? `${total ? Math.round((active.value / total) * 100) : 0}% of reported statuses`
                : "Across reported statuses"}
            </span>
            <Link
              to={
                active
                  ? `${UNITS}?status=${encodeURIComponent(active.key)}`
                  : UNITS
              }
            >
              {active ? "View matching vehicles" : "View vehicles"}
              <ArrowForwardRounded aria-hidden="true" />
            </Link>
          </footer>
        </>
      ) : (
        <p className={styles.message}>No vehicle status data is available.</p>
      )}
    </div>
  );
};
export default VehicleStatus;
